import { t } from '../i18n';
/**
 * Apps (Phase 16 Step 5): the catalogue shown by "New App", legacy default configs, and an offline prayer-time
 * calculation for the live preview.
 *
 * The catalogue is data so adding an app is one entry (C5). Names follow legacy CODOT ("Time", never "Clock").
 */
import type { WidgetConfig, WidgetType } from '@codot/contracts';

export type AppCategory = 'info' | 'messages' | 'business' | 'web';
export interface AppEntry {
  type: WidgetType;
  label: string;
  icon: string;
  description: string;
  /** Phase 17.11: groups the catalogue. */
  category: AppCategory;
}

/** Labels and descriptions follow the UI language (getters read the active locale each time). */
const entry = (type: WidgetType, icon: string, category: AppCategory): AppEntry => ({
  type,
  icon,
  category,
  get label() {
    return t(`appsCat.${type}`);
  },
  get description() {
    return t(`appsCat.${type}Desc`);
  },
});
export const APP_CATALOGUE: readonly AppEntry[] = [
  entry('time', '🕒', 'info'),
  entry('world_clock', '🌍', 'info'),
  entry('prayer_time', '🕌', 'info'),
  entry('weather', '⛅', 'info'),
  entry('countdown', '⏳', 'info'),
  entry('calendar', '📅', 'info'),
  entry('text', '📝', 'messages'),
  entry('ticker', '📢', 'messages'),
  entry('rss', '📰', 'messages'),
  entry('photos', '🖼️', 'messages'),
  entry('people', '👥', 'business'),
  entry('menu_board', '🍽️', 'business'),
  entry('chart', '📈', 'business'),
  entry('qr_code', '🔳', 'business'),
  entry('web_page', '🌐', 'web'),
  entry('youtube', '▶️', 'web'),
  entry('google_slides', '📊', 'web'),
  entry('dashboard', '📉', 'web'),
  /* Meeting room */
  entry('meeting_room', '🚪', 'business'),
  entry('stream', '📡', 'web'),
  // Social wall app
  entry('social_wall', '💬', 'messages'),
];
export const APP_CATEGORIES: AppCategory[] = ['info', 'messages', 'business', 'web'];

export const APP_LABELS = new Proxy({} as Record<WidgetType, string>, { get: (_, type) => t(`appsCat.${String(type)}`) });
export const APP_ICONS = Object.fromEntries(APP_CATALOGUE.map((a) => [a.type, a.icon])) as Record<WidgetType, string>;

const LOOK = { background: '#0F387A', foreground: '#FFFFFF' } as const;

/** Legacy "Customize App" defaults: Arabic, AM/PM, Clock Round, Player Location, Celsius, RSS Large/Slow/LTR. */
export function defaultConfig(type: WidgetType): WidgetConfig {
  switch (type) {
    case 'time':
      return {
        type,
        format: '12h',
        // Seconds on: the round clock gets its seconds hand ("Hide seconds" turns it off).
        showSeconds: true,
        showDate: true,
        timezone: null,
        style: 'round',
        showLocation: true,
        showTime: true,
        language: 'ar',
        ...LOOK,
      };
    case 'prayer_time':
      return {
        type,
        location: { source: 'screen' },
        calculationMethod: 'GULF',
        asrMethod: 'standard',
        highlightNext: true,
        language: 'ar',
        ...LOOK,
      };
    case 'weather':
      return {
        type,
        location: { source: 'screen' },
        units: 'metric',
        forecastDays: 3,
        refreshIntervalSec: 1800,
        language: 'ar',
        ...LOOK,
      };
    case 'countdown': {
      const d = new Date(Date.now() + 7 * 86_400_000);
      const pad = (n: number) => String(n).padStart(2, '0');
      return {
        type,
        title: '',
        target: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`,
        timezone: null,
        mode: 'down',
        showSeconds: true,
        doneText: '',
        language: 'en',
        ...LOOK,
      };
    }
    case 'qr_code':
      return { type, data: '', caption: '', captionPosition: 'below', ...LOOK };
    case 'text':
      return { type, headline: '', body: '', size: 'large', align: 'center', direction: 'ltr', ...LOOK };
    case 'ticker':
      return { type, messages: [], speed: 'medium', size: 'large', direction: 'ltr', separator: '•', ...LOOK };
    case 'menu_board':
      return {
        type,
        title: '',
        sections: [{ name: '', items: [{ name: '', description: '', price: '' }] }],
        columns: 2,
        direction: 'ltr',
        accent: '#F5B301',
        background: '#1B1B1F',
        foreground: '#FFFFFF',
      };
    case 'world_clock':
      return {
        type,
        cities: [
          { label: 'Dubai', timezone: 'Asia/Dubai' },
          { label: 'London', timezone: 'Europe/London' },
          { label: 'New York', timezone: 'America/New_York' },
        ],
        format: '24h',
        showDate: false,
        language: 'en',
        ...LOOK,
      };
    case 'web_page':
      return { type, url: '', refreshIntervalSec: 0, zoom: 100, interactive: false };
    case 'youtube':
      return { type, videoId: null, playlistId: null, muted: true, loop: true, captions: false };
    case 'google_slides':
      return { type, presentationId: '', slideSeconds: 8, loop: true };
    case 'dashboard':
      return { type, url: '', embedUrl: '', provider: 'other', refreshIntervalSec: 900, zoom: 100, theme: 'dark' };
    case 'people':
      return {
        type,
        feedId: '',
        style: 'team',
        title: '',
        nameColumn: '',
        photoColumn: '',
        roleColumn: '',
        detailColumn: '',
        extraColumn: '',
        dateColumn: '',
        birthdayRange: 'week',
        columns: 4,
        rows: 2,
        slideSeconds: 10,
        accent: '#F5B301',
        language: 'en',
        ...LOOK,
      };
    case 'photos':
      return {
        type,
        feedId: '',
        style: 'slideshow',
        title: '',
        imageColumn: '',
        captionColumn: '',
        slideSeconds: 6,
        fit: 'cover',
        wallColumns: 3,
        wallRows: 2,
        shuffle: false,
        maxPhotos: 30,
        direction: 'ltr',
        background: '#111111',
        foreground: '#FFFFFF',
      };
    case 'chart':
      return {
        type,
        feedId: '',
        kind: 'column',
        title: '',
        labelColumn: '',
        valueColumns: [],
        maxRows: 12,
        sort: 'none',
        showValues: true,
        prefix: '',
        suffix: '',
        decimals: 0,
        colors: ['#F5B301', '#38BDF8', '#F472B6', '#34D399'],
        direction: 'ltr',
        ...LOOK,
      };
    case 'calendar':
      return { type, icalUrl: '', title: '', daysAhead: 7, maxEvents: 8, format: '24h', showLocation: true, refreshIntervalSec: 900, language: 'en', accent: '#F5B301', ...LOOK };
    case 'rss':
      return {
        type,
        feedUrl: '',
        maxItems: 5,
        itemDurationMs: RSS_ITEM_MS.slow,
        showImages: false,
        refreshIntervalSec: 900,
        fontSize: 'large',
        speed: 'slow',
        direction: 'ltr',
        ...LOOK,
      };
    /* Meeting room */
    case 'meeting_room':
      return {
        type,
        icalUrl: '',
        roomName: '',
        hideTitles: false,
        showOrganizer: true,
        maxUpcoming: 4,
        format: '24h',
        layout: 'auto',
        freeColor: '#16A34A',
        busyColor: '#DC2626',
        refreshIntervalSec: 300,
        language: 'en',
        background: '#111827',
        foreground: '#FFFFFF',
      };
    case 'stream':
      return { type, url: '', muted: true, fit: 'contain', posterUrl: '', fallbackText: '', language: 'en', ...LOOK };
    // Social wall app
    case 'social_wall':
      return {
        type,
        feedId: '',
        style: 'wall',
        title: '',
        textColumn: '',
        authorColumn: '',
        handleColumn: '',
        avatarColumn: '',
        imageColumn: '',
        dateColumn: '',
        platformColumn: '',
        maxPosts: 20,
        columns: 3,
        rows: 2,
        slideSeconds: 10,
        imagesOnly: false,
        showPlatform: true,
        accent: '#1D9BF0',
        language: 'en',
        background: '#0F172A',
        foreground: '#FFFFFF',
      };
  }
}

/** Legacy "Speed" → how long each headline stays (also sent as `itemDurationMs` for older players). */
export const RSS_ITEM_MS = { slow: 12_000, medium: 8_000, fast: 5_000 } as const;

const method = (value: Extract<WidgetConfig, { type: 'prayer_time' }>['calculationMethod']) => ({
  value,
  get label() {
    return t(`appsCat.method_${value}`);
  },
});
export const CALCULATION_METHODS: { value: Extract<WidgetConfig, { type: 'prayer_time' }>['calculationMethod']; label: string }[] = [
  method('GULF'),
  method('MAKKAH'),
  method('MWL'),
  method('EGYPT'),
  method('KARACHI'),
  method('ISNA'),
  method('SINGAPORE'),
  method('TEHRAN'),
  method('JAFARI'),
];

/* ---------------- prayer times (port of the player's core/widget/PrayerTimes.kt) ---------------- */

export type Prayer = 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha';
type Method = { fajr: number; isha: number | null; ishaAfterMaghribMin?: number; maghrib?: number };
const METHODS: Record<string, Method> = {
  MWL: { fajr: 18, isha: 17 },
  ISNA: { fajr: 15, isha: 15 },
  EGYPT: { fajr: 19.5, isha: 17.5 },
  MAKKAH: { fajr: 18.5, isha: null, ishaAfterMaghribMin: 90 },
  KARACHI: { fajr: 18, isha: 18 },
  TEHRAN: { fajr: 17.7, isha: 14, maghrib: 4.5 },
  JAFARI: { fajr: 16, isha: 14, maghrib: 4 },
  GULF: { fajr: 19.5, isha: null, ishaAfterMaghribMin: 90 },
  SINGAPORE: { fajr: 20, isha: 18 },
};
const SUN_DISC = 0.833;
const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;
const fixAngle = (a: number) => ((a % 360) + 360) % 360;
const fixHour = (h: number) => ((h % 24) + 24) % 24;

/**
 * Same formulation and iteration as the player, so the preview shows the times the screen will show.
 * `date` is a calendar day (only its Y/M/D are used, as UTC). Returns epoch ms, or null when the sun never
 * reaches the angle (high latitudes).
 */
export function prayerTimes(
  date: { year: number; month: number; day: number },
  lat: number,
  lon: number,
  method: string,
  asr: 'standard' | 'hanafi',
): Record<Prayer, number | null> {
  const m = METHODS[method] ?? METHODS.MWL;
  const asrFactor = asr === 'hanafi' ? 2 : 1;
  const epochDay = Date.UTC(date.year, date.month - 1, date.day) / 86_400_000;
  const jd0 = epochDay + 2440587.5;
  const sun = (t: number): [number, number] => {
    const d = jd0 + t / 24 - 2451545.0;
    const g = fixAngle(357.529 + 0.98560028 * d);
    const q = fixAngle(280.459 + 0.98564736 * d);
    const l = fixAngle(q + 1.915 * Math.sin(rad(g)) + 0.02 * Math.sin(rad(2 * g)));
    const e = 23.439 - 0.00000036 * d;
    const ra = fixHour(deg(Math.atan2(Math.cos(rad(e)) * Math.sin(rad(l)), Math.cos(rad(l)))) / 15);
    const eqt = q / 15 - ra;
    return [deg(Math.asin(Math.sin(rad(e)) * Math.sin(rad(l)))), eqt - 24 * Math.round(eqt / 24)];
  };
  const noon = (t: number) => 12 - sun(t)[1] - lon / 15;
  const angleTime = (angle: number, t: number, before: boolean): number | null => {
    const decl = sun(t)[0];
    const c = (-Math.sin(rad(angle)) - Math.sin(rad(lat)) * Math.sin(rad(decl))) / (Math.cos(rad(lat)) * Math.cos(rad(decl)));
    if (Math.abs(c) > 1) return null;
    const h = deg(Math.acos(c)) / 15;
    return before ? noon(t) - h : noon(t) + h;
  };
  const asrTime = (t: number) => {
    const decl = sun(t)[0];
    return angleTime(-deg(Math.atan(1 / (asrFactor + Math.tan(rad(Math.abs(lat - decl)))))), t, false);
  };

  let t = [5, 6, 12, 13, 18, 18, 18].map((h) => h - lon / 15);
  let out: (number | null)[] = [];
  for (let i = 0; i < 3; i++) {
    const fajr = angleTime(m.fajr, t[0], true);
    const sunrise = angleTime(SUN_DISC, t[1], true);
    const dhuhr = noon(t[2]);
    const asrT = asrTime(t[3]);
    const sunset = angleTime(SUN_DISC, t[4], false);
    const maghrib = m.maghrib != null ? angleTime(m.maghrib, t[5], false) : sunset;
    const isha =
      m.ishaAfterMaghribMin != null ? (maghrib == null ? null : maghrib + m.ishaAfterMaghribMin / 60) : angleTime(m.isha!, t[6], false);
    out = [fajr, sunrise, dhuhr, asrT, sunset, maghrib, isha];
    t = t.map((v, k) => out[k] ?? v);
  }
  const at = (h: number | null) => (h == null ? null : (epochDay * 86_400 + Math.round(h * 3600)) * 1000);
  return { fajr: at(out[0]), sunrise: at(out[1]), dhuhr: at(out[2]), asr: at(out[3]), maghrib: at(out[5]), isha: at(out[6]) };
}

